import React, { useState, useEffect } from 'react';
import { ChartBarIcon, UsersIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

const AppraisalReports = () => {
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [overallReport, setOverallReport] = useState(null);
  const [managerReport, setManagerReport] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActiveCycle();
  }, []);

  const fetchActiveCycle = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/appraisal/active-cycle');
      const data = await response.json();
      setSelectedCycle(data.cycle_id);
      fetchReports(data.cycle_id);
    } catch (error) {
      console.error('Error fetching active cycle:', error);
      setLoading(false);
    }
  };

  const fetchReports = async (cycleId) => {
    try {
      const [overallRes, managerRes] = await Promise.all([
        fetch(`http://localhost:8000/api/admin/appraisal-report/${cycleId}`),
        fetch(`http://localhost:8000/api/admin/appraisal-report-per-manager/${cycleId}`)
      ]);

      const overallData = await overallRes.json();
      const managerData = await managerRes.json();

      setOverallReport(overallData);
      setManagerReport(managerData);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading reports...</div>;
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Appraisals</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{overallReport.total_appraisals}</p>
              </div>
              <UsersIcon className="h-12 w-12 text-indigo-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Goals Assigned</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{overallReport.goals_assigned}</p>
              </div>
              <ClockIcon className="h-12 w-12 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Employee Submitted</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{overallReport.employee_submitted}</p>
              </div>
              <ChartBarIcon className="h-12 w-12 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Released</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{overallReport.released}</p>
              </div>
              <CheckCircleIcon className="h-12 w-12 text-green-600" />
            </div>
          </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Manager-wise Progress</h2>
        {managerReport.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No manager data available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manager</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Team Members</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {managerReport.map((manager) => {
                  const progress = manager.total_team_members > 0 
                    ? Math.round((manager.completed / manager.total_team_members) * 100) 
                    : 0;
                  
                  return (
                    <tr key={manager.manager_emp_code} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{manager.manager_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{manager.manager_emp_code}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{manager.total_team_members}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{manager.completed}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-indigo-600 h-2 rounded-full" 
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{progress}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppraisalReports;
